import { area, booleanContains, booleanEqual, booleanOverlap, featureCollection, kinks, lineString, point, polygon, union, } from '@turf/turf';
const MIN_RING_AREA_SQUARE_METERS = 1_000_000;
const closeRing = (ring) => {
    const firstCoordinate = ring[0];
    const lastCoordinate = ring[ring.length - 1];
    if (booleanEqual(point(firstCoordinate), point(lastCoordinate))) {
        return ring;
    }
    return [...ring, firstCoordinate];
};
const sanitizeRing = (ring) => {
    if (!ring || ring.length <= 4) {
        return null;
    }
    const clonedRing = ring.map((coordinate) => [...coordinate]);
    try {
        if (kinks(lineString(clonedRing)).features.length > 0) {
            return null;
        }
        if (area(polygon([clonedRing])) <= MIN_RING_AREA_SQUARE_METERS) {
            return null;
        }
        return closeRing(clonedRing);
    }
    catch {
        return null;
    }
};
const sanitizePolygon = (feature) => {
    if (feature.geometry.type !== 'Polygon') {
        return null;
    }
    const [exteriorRing, ...interiorRings] = feature.geometry.coordinates;
    const sanitizedExteriorRing = sanitizeRing(exteriorRing);
    if (!sanitizedExteriorRing) {
        return null;
    }
    const sanitizedInteriorRings = interiorRings.map(sanitizeRing).filter((ring) => ring !== null);
    return polygon([sanitizedExteriorRing, ...sanitizedInteriorRings], feature.properties ?? {});
};
const removeExactDuplicates = (features) => {
    const seen = new Set();
    return features.filter((feature) => {
        const geometryKey = JSON.stringify(feature.geometry.coordinates);
        if (seen.has(geometryKey)) {
            return false;
        }
        seen.add(geometryKey);
        return true;
    });
};
const polygonsAreConnected = (first, second) => {
    try {
        return (booleanEqual(first, second) || booleanOverlap(first, second) || booleanContains(first, second) || booleanContains(second, first));
    }
    catch {
        return false;
    }
};
const findRoot = (parents, index) => {
    if (parents[index] !== index) {
        parents[index] = findRoot(parents, parents[index]);
    }
    return parents[index];
};
const joinComponents = (parents, firstIndex, secondIndex) => {
    const firstRoot = findRoot(parents, firstIndex);
    const secondRoot = findRoot(parents, secondIndex);
    if (firstRoot !== secondRoot) {
        parents[secondRoot] = firstRoot;
    }
};
const groupConnectedPolygons = (features) => {
    const parents = features.map((_, index) => index);
    for (let firstIndex = 0; firstIndex < features.length; firstIndex++) {
        for (let secondIndex = firstIndex + 1; secondIndex < features.length; secondIndex++) {
            if (polygonsAreConnected(features[firstIndex], features[secondIndex])) {
                joinComponents(parents, firstIndex, secondIndex);
            }
        }
    }
    const groups = new Map();
    features.forEach((feature, index) => {
        const root = findRoot(parents, index);
        const group = groups.get(root) ?? [];
        group.push(feature);
        groups.set(root, group);
    });
    return [...groups.values()];
};
const mergeConnectedGroup = (group) => {
    if (group.length < 2) {
        return group;
    }
    let merged = group[0];
    const pending = group.slice(1);
    while (pending.length > 0) {
        const nextIndex = pending.findIndex((feature) => polygonsAreConnected(merged, feature));
        if (nextIndex === -1) {
            return group;
        }
        const next = pending[nextIndex];
        try {
            const result = union(featureCollection([merged, next]));
            if (!result || result.geometry.type !== 'Polygon') {
                return group;
            }
            merged = polygon(result.geometry.coordinates, result.properties ?? {});
            pending.splice(nextIndex, 1);
        }
        catch {
            return group;
        }
    }
    return [merged];
};
export const simplifyZoneFeatures = (features) => {
    const polygons = removeExactDuplicates(features.map(sanitizePolygon).filter((feature) => feature !== null));
    return groupConnectedPolygons(polygons).flatMap(mergeConnectedGroup);
};
//# sourceMappingURL=simplify-zone-features.js.map